import Player, { VimeoChapter } from '@vimeo/player';
import {
  ArrowLeftStroke,
  ClockAltStroke,
  ClockStroke,
  TargetStroke,
  WeightStroke,
  XStroke,
} from 'assets/icons';
import ChapterCard from 'components/Cards/ChapterCard';
import { useEffect, useRef, useState } from 'react';
import { PhaseType } from 'types/package';
import {
  FlexWrapper,
  StyledAuthor,
  StyledAuthorTitle,
  StyledAvatar,
  StyledAvatarWrapper,
  StyledCardWrapper,
  StyledContentWrapper,
  StyledDescriptionField,
  StyledDescriptionWrapper,
  StyledHeading,
  StyledIconWrapper,
  StyledLinkButton,
  StyledNav,
  StyledSectionHeader,
  VideoContainer,
  Wrapper,
} from './styles';

type VideoPlayerProps = {
  time: string;
  title: string;
  author: string;
  authorTitle: string;
  phase: PhaseType;
  videoId: string;
  equipment: string;
  goal: string;
  careId: string;
  avatar: string;
  avatarAlt: string;
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  time,
  avatar,
  title,
  author,
  authorTitle,
  phase,
  videoId,
  equipment,
  goal,
  careId,
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const [chapters, setChapters] = useState<VimeoChapter[]>([]);
  const [videoPlayer, setVideoPlayer] = useState<Player>();
  const [activeChapter, setActiveChapter] = useState<number>(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const getChapters = async () => {
    const videoChapters = await videoPlayer?.getChapters();
    setChapters(videoChapters || []);
  };

  const handleCardClick = (i: number) => {
    if (isPlaying && i === activeChapter) {
      videoPlayer?.pause();
      return;
    }
    if (i === activeChapter) {
      videoPlayer?.play();
      return;
    }
    videoPlayer?.play();
    videoPlayer?.setCurrentTime(chapters[i].startTime);
  };

  useEffect(() => {
    if (videoRef.current) {
      const width = () => {
        if (window.innerWidth < 1026) {
          return window.innerWidth;
        }
        if (window.innerWidth < 1400) {
          return window.innerWidth - 450;
        }
        return 950;
      };
      const video = new Player(videoRef.current as HTMLDivElement, {
        id: +videoId,
        width: width(),
      });
      setVideoPlayer(video);
      // Vimeo index starts at 1. Subtracting 1 to match our map index.
      videoPlayer?.on('chapterchange', e => setActiveChapter(e.index - 1));
      videoPlayer?.on('play', () => setIsPlaying(true));
      videoPlayer?.on('pause', () => setIsPlaying(false));
      getChapters();
    }
    return () => {
      videoPlayer?.destroy();
    };
  }, [videoRef.current]);

  return (
    <Wrapper>
      <div>
        <StyledNav>
          <StyledLinkButton to={`/care/${careId}`}>
            <ArrowLeftStroke color="white" />
          </StyledLinkButton>
        </StyledNav>
        <VideoContainer ref={videoRef} isPlaying={isPlaying}></VideoContainer>
        <StyledDescriptionWrapper>
          <StyledHeading>{title}</StyledHeading>
          <StyledAvatarWrapper>
            <StyledAvatar src={avatar} />
            <div>
              <StyledAuthor>{author}</StyledAuthor>
              <StyledAuthorTitle>{authorTitle}</StyledAuthorTitle>
            </div>
          </StyledAvatarWrapper>
          <StyledSectionHeader>Description</StyledSectionHeader>
          <FlexWrapper>
            <StyledIconWrapper phase={phase}>
              <ClockStroke />
            </StyledIconWrapper>
            <StyledDescriptionField>{time}</StyledDescriptionField>
          </FlexWrapper>
          <FlexWrapper>
            <StyledIconWrapper phase={phase}>
              <WeightStroke />
            </StyledIconWrapper>
            <StyledDescriptionField>{equipment}</StyledDescriptionField>
          </FlexWrapper>
          <FlexWrapper>
            <StyledIconWrapper phase={phase}>
              <TargetStroke />
            </StyledIconWrapper>
            <StyledDescriptionField>{goal}</StyledDescriptionField>
          </FlexWrapper>
        </StyledDescriptionWrapper>
      </div>
      <StyledCardWrapper>
        <StyledSectionHeader>Exercises</StyledSectionHeader>
        {chapters?.map((chapter, i) => {
          const [title, description] = chapter.title.split('|');
          return (
            <ChapterCard
              isActive={activeChapter === i}
              isPlaying={isPlaying && activeChapter === i}
              key={chapter.startTime}
              heading={title}
              description={description}
              handleClick={handleCardClick}
              cardIndex={i}
              phase={phase}
            />
          );
        })}
      </StyledCardWrapper>
    </Wrapper>
  );
};

export default VideoPlayer;
